import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { IrisIconComponent, IconSizes, IconColors } from '@sondermind/iris/icon';

const TRY_SUGGESTIONS = [
  'What is a W-9?',
  'Why is my tax classification set this way?',
  'What is my NPI number?',
  'How do I update my address?',
];

const CYCLE_MS = 3000;
const SWAP_MS = 350;
const SLIDE_IN_DELAY_MS = 30;

@Component({
  selector: 'member-w9-agent-composer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IrisIconComponent],
  styleUrls: ['./w9-agent-composer.component.scss'],
  templateUrl: './w9-agent-composer.component.html',
})
export class W9AgentComposerComponent implements AfterViewInit {
  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();
  @Output() send = new EventEmitter<string>();
  @ViewChild('tryTextEl') tryTextElRef?: ElementRef<HTMLSpanElement>;

  readonly iconSizes = IconSizes;
  readonly iconColors = IconColors;
  readonly TRY_SUGGESTIONS = TRY_SUGGESTIONS;
  readonly focused = signal(false);

  readonly tryIndex = signal(0);

  // The TRY hint shows whenever the field is idle: not focused and empty.
  // It returns after the field loses focus (it is not dismissed permanently).
  // A getter (not computed) because `value` is a plain @Input, not a signal.
  get showTry(): boolean {
    return !this.focused() && !this.value;
  }

  private cycleId: ReturnType<typeof setInterval> | null = null;
  private swapId: ReturnType<typeof setTimeout> | null = null;
  private slideInId: ReturnType<typeof setTimeout> | null = null;
  private readonly destroyRef = inject(DestroyRef);

  ngAfterViewInit(): void {
    this.cycleId = setInterval(() => {
      const el = this.tryTextElRef?.nativeElement;
      if (!el) return;

      // Phase 1: slide up and fade out
      el.style.opacity = '0';
      el.style.transform = 'translateY(-6px)';

      this.swapId = setTimeout(() => {
        // Phase 2: swap text, reposition below
        this.tryIndex.set((this.tryIndex() + 1) % TRY_SUGGESTIONS.length);
        el.textContent = TRY_SUGGESTIONS[this.tryIndex()];
        el.style.transform = 'translateY(6px)';

        this.slideInId = setTimeout(() => {
          // Phase 3: slide up into place and fade in
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, SLIDE_IN_DELAY_MS);
      }, SWAP_MS);
    }, CYCLE_MS);

    this.destroyRef.onDestroy(() => this.clearTimers());
  }

  private clearTimers(): void {
    if (this.cycleId) clearInterval(this.cycleId);
    if (this.swapId) clearTimeout(this.swapId);
    if (this.slideInId) clearTimeout(this.slideInId);
    this.cycleId = null;
    this.swapId = null;
    this.slideInId = null;
  }

  onFocus(): void {
    this.focused.set(true);
  }

  onBlur(): void {
    this.focused.set(false);
  }

  onInput(e: Event): void {
    const v = (e.target as HTMLInputElement).value;
    this.value = v;
    this.valueChange.emit(v);
  }

  onSubmit(): void {
    const trimmed = this.value.trim();
    if (trimmed) this.send.emit(trimmed);
  }
}
